
import { AiPromptsManager } from "@/components/ai/ai-prompts-manager";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SectionDeveloper } from "@/pages/settings/components/section-developer";
import { SectionGeneral } from "@/pages/settings/components/section-general";
import { SectionPrivacy } from "@/pages/settings/components/section-privacy";
import { SectionProject } from "@/pages/settings/components/section-project";
import { PromptScope } from "@/services/supabase/ai-prompts";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { Code2, Database, Palette, Shield, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useLicense } from '@/hooks/useLicense';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState("general");
  const { hasAIFeatures } = useLicense();

  const tabContentVariant: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
  };

  return (
    <div className="flex flex-col h-full w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 overflow-hidden">
      <div className="container mx-auto max-w-6xl py-6 flex-none px-4 md:px-6">
        <div className="mb-6 space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">Settings</h2>
          <p className="text-sm text-muted-foreground">
            Manage your account settings and preferences.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${hasAIFeatures ? 'grid-cols-5' : 'grid-cols-4'} lg:w-[750px] mb-4`}>
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              General
            </TabsTrigger>
            {hasAIFeatures && (
              <TabsTrigger value="ai" className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                AI Assistant
              </TabsTrigger>
            )}
            <TabsTrigger value="project" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Project
            </TabsTrigger>
            <TabsTrigger value="developer" className="flex items-center gap-2">
              <Code2 className="h-4 w-4" />
              Developer
            </TabsTrigger>
            <TabsTrigger value="privacy" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Privacy
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <ScrollArea className="flex-1 w-full">
        <div className="container mx-auto max-w-6xl pb-10">
          <AnimatePresence mode="wait">
            {activeTab === "general" && (
              <motion.div
                key="general"
                variants={tabContentVariant}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <SectionGeneral />
              </motion.div>
            )}
            {activeTab === "project" && (
              <motion.div
                key="project"
                variants={tabContentVariant}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <SectionProject />
              </motion.div>
            )}
            {activeTab === "developer" && (
              <motion.div
                key="developer"
                variants={tabContentVariant}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <SectionDeveloper />
              </motion.div>
            )}
            {activeTab === "privacy" && (
              <motion.div
                key="privacy"
                variants={tabContentVariant}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <SectionPrivacy />
              </motion.div>
            )}
            {hasAIFeatures && activeTab === "ai" && (
              <motion.div
                key="ai"
                variants={tabContentVariant}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Card className="border-muted bg-card/50">
                  <CardHeader>
                    <CardTitle>Global AI Assistant Settings</CardTitle>
                    <CardDescription>Configure AI behavior across your entire workspace.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <AiPromptsManager
                      scope={PromptScope.Global}
                      title="Global AI Assistant Prompts"
                      description="Configure system instructions that apply to the AI assistant everywhere."
                    />
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </ScrollArea>
    </div>
  );
}